// src/components/MissingParticipants.js
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import mentoringApi from '../axiosInstance';

/**
 * Endpunkte
 * GET    {API_BASE}/mentoring/match-ready/missing?survey_id=21&role=all&reason=all
 * GET    {API_BASE}/mentoring/match-ready/stats?survey_id=21
 * DELETE {MENTORING_API}/mentoring/hr/users/:id  Body: { hard: true }
 * POST   {MENTORING_API}/mentoring/hr/users/:id/undo-link
 */

const ROLE_TABS = [
  { key: 'all',     labelKey: 'all',     fallbackDe: 'Alle',     fallbackEn: 'All' },
  { key: 'mentee',  labelKey: 'mentees', fallbackDe: 'Mentees',  fallbackEn: 'Mentees' },
  { key: 'mentor',  labelKey: 'mentors', fallbackDe: 'Mentoren', fallbackEn: 'Mentors' }
];

const REASON_TABS = [
  { key: 'all',        labelKey: 'all',        fallbackDe: 'Alle Gründe',           fallbackEn: 'All reasons' },
  { key: 'onboarding', labelKey: 'onboarding', fallbackDe: 'Onboarding fehlt',      fallbackEn: 'Onboarding missing' },
  { key: 'profile',    labelKey: 'profile',    fallbackDe: 'Profil unvollständig',  fallbackEn: 'Profile incomplete' },
  { key: 'analysis',   labelKey: 'analysis',   fallbackDe: 'Analyse fehlt',         fallbackEn: 'Analysis missing' }
];

/* Dringlichkeits-Filter */
const URGENCY_TABS = [
  { key: 'all',      fallbackDe: 'Alle',            fallbackEn: 'All' },
  { key: 'soon',     fallbackDe: 'Bald fällig',     fallbackEn: 'Due soon' },   // 3–7 Tage
  { key: 'due',      fallbackDe: 'Überfällig',      fallbackEn: 'Overdue' },    // ≥ 7 Tage
  { key: 'critical', fallbackDe: 'Kritisch',        fallbackEn: 'Critical' },   // ≥ 14 Tage
  { key: 'ok',       fallbackDe: 'Aktiv (<3 Tage)', fallbackEn: 'Active (<3 days)' }
];

const URGENCY_RANK = { critical: 3, due: 2, soon: 1, ok: 0, unknown: -1 };

function classNames(...cn) { return cn.filter(Boolean).join(' '); }

function formatDateShort(s, locale = 'de') {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale);
}
function formatDateTime(s, locale = 'de-DE') {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, { timeZone: 'Europe/Berlin' });
}

/* Deep Links */
function getDeepLink({ role, reason, lang = 'de' }) {
  if (role === 'mentee') {
    if (reason === 'onboarding' || reason === 'profile')
      return `/#/onboarding-mentee?lang=${lang}`;;
    if (reason === 'analysis')
      return `https://localhost:3003/#/mentee-dashboard?lang=${lang}`;
  }
  if (role === 'mentor') {
    if (reason === 'onboarding' || reason === 'profile')
      return `https://localhost:3003/#/onboarding-mentor?lang=${lang}`;
    if (reason === 'analysis')
      return `https://localhost:3003/#/mentor-dashboard?lang=${lang}`;
  }
  return `https://localhost:3003/?lang=${lang}`;
}

/* Mail-Text zusammenbauen (DE/EN Fallbacks) */
function tDefault(t, lang, key, de, en) {
  return t(key, lang === 'de' ? de : en);
}
function buildMail({ t, person, reasons, hrName, lang }) {
  const reasonText = (reasons || [])
    .map((r) => {
      if (r === 'onboarding') return tDefault(t, lang, 'missing.reminder.reason.onboarding', 'Onboarding', 'Onboarding');
      if (r === 'profile')    return tDefault(t, lang, 'missing.reminder.reason.profile',    'Profil',     'Profile');
      if (r === 'analysis')   return tDefault(t, lang, 'missing.reminder.reason.analysis',   'Analyse',    'Analysis');
      return r;
    })
    .join(', ');

  const link = getDeepLink({ role: person.role, reason: reasons?.[0], lang });

  const subject = tDefault(
    t, lang, 'missing.reminder.subject',
    'Kurze Erinnerung: Bitte Onboarding/Profil/Analyse abschließen',
    'Quick reminder: please complete onboarding/profile/analysis'
  );

  const greeting = tDefault(t, lang, 'missing.reminder.greeting', 'Hallo', 'Hi');
  const firstName = (person.first_name || '').trim();

  const thanks = tDefault(
    t, lang, 'missing.reminder.thanks',
    'Vielen Dank! Bei Fragen gern kurz antworten.',
    'Thanks! If you have questions, just reply to this email.'
  );

  const regards = tDefault(t, lang, 'missing.reminder.regards', 'Beste Grüße', 'Best regards');

  const body = [
    (firstName ? `${greeting} ${firstName},` : `${greeting},`),
    '',
    tDefault(
      t, lang, 'missing.reminder.body',
      'damit wir dich fürs Mentoring-Matching berücksichtigen können, fehlt noch/fehlen noch:',
      'to include you in the mentoring matching, the following item(s) are still missing:'
    ),
    `• ${reasonText}.`,
    '',
    tDefault(t, lang, 'missing.reminder.hint', 'Du kannst es hier erledigen:', 'You can finish it here:'),
    link,
    '',
    thanks,
    `${regards}\n${hrName || 'HR'}`
  ].join('\n');

  return { subject, body, link };
}

/* ---------- Gilt-als-gelöscht?-Prüfung (Frontend-Filter) ---------- */
function isDeletedUser(p) {
  if (!p) return false;
  const email = (p.email || '').toLowerCase();
  const first = (p.first_name || '').toLowerCase();

  return Boolean(p.is_deleted) ||
         Boolean(p.deleted_at) ||
         email.endsWith('@example.invalid') ||
         email.startsWith('deleted+') ||
         first === 'deleted';
}

/* Inaktivität berechnen */
function computeInactivity(person) {
  const apiLastAt = person.last_action_at || person.last_activity_at || person.last_update_at;
  let lastAt = apiLastAt || person.last_login || null;

  let days = person.days_inactive;
  if (days == null) {
    if (!lastAt) days = null;
    else {
      const ms = Date.now() - new Date(lastAt).getTime();
      days = Math.max(0, Math.floor(ms / 86400000));
    }
  }

  let urgency = person.urgency;
  if (!urgency) {
    if (days == null) urgency = 'unknown';
    else if (days >= 14) urgency = 'critical';
    else if (days >= 7)  urgency = 'due';
    else if (days >= 3)  urgency = 'soon';
    else                 urgency = 'ok';
  }
  return { lastAt, days, urgency };
}

/* Kleine Badges */
function Badge({ ok, label, lang = 'de' }) {
  const suffix = ok ? (lang === 'de' ? ' ok' : ' ok') : (lang === 'de' ? ' fehlt' : ' missing');
  return (
    <span
      className={classNames(
        'text-xs px-2 py-0.5 rounded-full',
        ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      )}
      aria-label={label + suffix}
    >
      {label}
    </span>
  );
}

function InactivityBadge({ days, lastAt, urgency, lang = 'de' }) {
  const label = lang === 'de'
    ? `Inaktiv: ${days ?? '—'} Tage`
    : `Inactive: ${days ?? '—'} days`;

  const title = (lang === 'de' ? 'Letzte Aktion: ' : 'Last action: ')
    + (formatDateTime(lastAt, lang === 'de' ? 'de-DE' : 'en-US'));

  const color =
    urgency === 'critical' ? 'bg-red-600 text-white' :
    urgency === 'due'      ? 'bg-orange-500 text-black' :
    urgency === 'soon'     ? 'bg-yellow-400 text-black' :
                             'bg-gray-600 text-white';

  return (
    <span className={classNames('text-xs px-2 py-0.5 rounded-full', color)} title={title}>
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */

const API_BASE =
  (process.env.REACT_APP_MENTORING_API_BASE?.replace(/\/$/, '')) || 'https://localhost:3006';

export default function MissingParticipants({ surveyId, hrName, hrEmail }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'de').toLowerCase().startsWith('de') ? 'de' : 'en';
  const tr = (key, de, en) => t(key, lang === 'de' ? de : en);

  const [roleTab, setRoleTab] = useState('all');
  const [reasonTab, setReasonTab] = useState('all');
  const [urgencyTab, setUrgencyTab] = useState('all');
  const [q, setQ] = useState('');

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState('');

  /* Lösch-Ladezustand (pro User-ID) */
  const [deletingIds, setDeletingIds] = useState(() => new Set());

  /* Undo-Link Ladezustand (pro User-ID) */
  const [undoIds, setUndoIds] = useState(() => new Set());

  useEffect(() => {
    if (!surveyId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  async function fetchData() {
    setLoading(true);
    setError('');

    const missingUrl = `${API_BASE}/mentoring/match-ready/missing`;
    const statsUrl   = `${API_BASE}/mentoring/match-ready/stats`;

    try {
      const [missingRes, statsRes] = await Promise.allSettled([
        axios.get(missingUrl, {
          params: { survey_id: surveyId, role: 'all', reason: 'all' },
          withCredentials: true
        }),
        axios.get(statsUrl, {
          params: { survey_id: surveyId },
          withCredentials: true
        })
      ]);

      if (statsRes.status === 'fulfilled' && statsRes.value?.data) {
        setStats(statsRes.value.data);
      } else {
        setStats(null);
      }

      if (missingRes.status === 'fulfilled' && Array.isArray(missingRes.value?.data)) {
        setList(missingRes.value.data);
      } else {
        setList([]);
      }
    } catch (e) {
      console.error('MissingParticipants fetch error:', e);
      setError(tr('missing.loadError', 'Fehler beim Laden der Teilnehmenden.', 'Error while loading participants.'));
      setList([]); setStats(null);
    } finally {
      setLoading(false);
    }
  }

  /* Enrichment: ZUERST gelöschte ausblenden, dann Inaktivität anreichern */
  const enriched = useMemo(() => {
    return (list || [])
      .filter(p => !isDeletedUser(p)) // <— gelöschte Nutzer ausblenden
      .map(p => {
        const ia = computeInactivity(p);
        return { ...p, _lastActionAt: ia.lastAt, _daysInactive: ia.days, _urgency: ia.urgency };
      });
  }, [list]);

  /* Counts für Dringlichkeits-Chips (auf Basis der gefilterten Liste) */
  const urgencyCounts = useMemo(() => {
    const c = { ok: 0, soon: 0, due: 0, critical: 0, unknown: 0 };
    for (const p of enriched) c[p._urgency] = (c[p._urgency] || 0) + 1;
    return c;
  }, [enriched]);

    /* Filter + Sort */
    const filtered = useMemo(() => {
      const data = (enriched || [])
        .filter(p => (roleTab === 'all' ? true : p.role === roleTab))

        // IMPORTANT: Undo-Kandidaten niemals durch Reason-Tab ausblenden
        .filter(p => (reasonTab === 'all' ? true : (p.missing?.includes(reasonTab) || isUndoCandidate(p))))

        .filter(p => (urgencyTab === 'all' ? true : p._urgency === urgencyTab))
        .filter((p) => {
          if (!q.trim()) return true;
          const s = q.toLowerCase();
          return (
            `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(s) ||
            (p.email || '').toLowerCase().includes(s) ||
            (p.abteilung || '').toLowerCase().includes(s) ||
            (p.job_title || '').toLowerCase().includes(s)
          );
        })

        // Sort: Undo immer nach oben, danach wie gehabt nach Dringlichkeit/Inaktivität
        .sort((a, b) =>
          (Number(isUndoCandidate(b)) - Number(isUndoCandidate(a))) ||
          (URGENCY_RANK[b._urgency] - URGENCY_RANK[a._urgency]) ||
          ((b._daysInactive ?? -1) - (a._daysInactive ?? -1))
        );

      return data;
    }, [enriched, roleTab, reasonTab, urgencyTab, q]);

  /* (optional) Grund-Zähler – aktuell noch auf der Roh-Liste.
     Kann bei Bedarf ebenfalls auf 'enriched' wechseln. */
  const listCounts = useMemo(() => {
    const c = { onboarding: 0, profile: 0, analysis: 0 };
    for (const p of list) (p.missing || []).forEach((r) => { if (c[r] !== undefined) c[r] += 1; });
    return c;
  }, [list]);
  const counters = useMemo(() => listCounts, [listCounts, stats]);

  function toggleSelect(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }
  function selectAllVisible() {
    const next = new Set(selected);
    filtered.forEach((p) => next.add(p.user_id));
    setSelected(next);
  }
  function clearSelection() { setSelected(new Set()); }

  function openMailClient(person) {
    const { subject, body } = buildMail({ t, person, reasons: person.missing || [], hrName, lang });
    const to = encodeURIComponent(person.email);
    const sub = encodeURIComponent(subject);
    const bod = encodeURIComponent(body);
    window.location.href = `mailto:${to}?subject=${sub}&body=${bod}`;
    toast.info(tr('missing.mailClient', 'E-Mail-Client wurde geöffnet.', 'Mail client opened.'));
  }

  async function copyMail(person) {
    const { subject, body } = buildMail({ t, person, reasons: person.missing || [], hrName, lang });
    const text = `To: ${person.email}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(tr('missing.copied', 'In Zwischenablage kopiert.', 'Copied to clipboard.'));
    } catch {
      toast.error(tr('missing.copyFailed', 'Konnte nicht kopiert werden.', 'Failed to copy.'));
    }
  }

  async function bulkCopy() {
    const selectedPeople = filtered.filter((p) => selected.has(p.user_id));
    if (selectedPeople.length === 0) {
      toast.error(tr('missing.selectFirst', 'Bitte wähle mindestens eine Person aus.', 'Please select at least one person.'));
      return;
    }
    const parts = selectedPeople.map((p) => {
      const { subject, body } = buildMail({ t, person: p, reasons: p.missing || [], hrName, lang });
      return `To: ${p.email}\nSubject: ${subject}\n\n${body}`;
    });
    try {
      await navigator.clipboard.writeText(parts.join('\n\n-----------------------------\n\n'));
      toast.success(tr('missing.bulkCopied', 'Vorlagen für Auswahl kopiert.', 'Templates for selection copied.'));
    } catch {
      toast.error(tr('missing.copyFailed', 'Konnte nicht kopiert werden.', 'Failed to copy.'));
    }
  }

  function exportCSV() {
    const header = lang === 'de'
      ? ['rolle', 'vorname', 'nachname', 'email', 'abteilung', 'jobtitel', 'letzte_aktion', 'tage_inaktiv', 'fehlt']
      : ['role', 'first_name', 'last_name', 'email', 'department', 'job_title', 'last_action', 'days_inactive', 'missing'];

    const rows = [
      header.join(';'),
      ...filtered.map((p) =>
        [
          p.role,
          p.first_name || '',
          p.last_name || '',
          p.email || '',
          p.abteilung || '',
          p.job_title || '',
          p._lastActionAt || p.last_login || '',
          p._daysInactive ?? '',
          (p.missing || []).join(',')
        ].join(';')
      )
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (lang === 'de'
      ? `teilnehmende_handlungsbedarf_s${surveyId}.csv`
      : `participants_action_needed_s${surveyId}.csv`);
    a.click();
    URL.revokeObjectURL(url);
    toast.success(tr('missing.csvExported', 'CSV exportiert.', 'CSV exported.'));
  }

  /* ---------- Einzellöschen mit hübschen Toasts ---------- */
  async function deletePerson(person, hard = true) {
    const id = person.user_id;
    setDeletingIds(prev => new Set(prev).add(id));

    const req = mentoringApi.delete(`/mentoring/hr/users/${id}`, {
      withCredentials: true,
      data: { hard }
    });

    try {
      await toast.promise(req, {
        loading: tr('missing.deleting', 'Lösche…', 'Deleting…'),
        success: tr('missing.deletedOk', 'Teilnehmender wurde gelöscht.', 'Participant deleted.'),
        error: tr('missing.deletedFail', 'Löschen fehlgeschlagen.', 'Delete failed.')
      });

      // Optimistic update: aus Liste & Auswahl entfernen
      setList(prev => prev.filter(p => p.user_id !== id));
      setSelected(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    } finally {
      setDeletingIds(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }

  function handleDeleteClick(person) {
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email;
    const msg = lang === 'de'
      ? `Soll "${fullName}" wirklich vollständig gelöscht werden?\n\nDies entfernt (falls vorhanden) Profile, Ziele/Kompetenzen, Paarungen/Matching-Ergebnisse und den Benutzerzugang. Dieser Schritt kann nicht rückgängig gemacht werden.`
      : `Do you really want to permanently delete "${fullName}"?\n\nThis removes (if present) profiles, goals/competences, pairings/matching results and the user account. This action cannot be undone.`;
    if (!window.confirm(msg)) return;
    deletePerson(person, true); // hard delete
  }

   /* ---------- Undo-Link senden (nur wenn sinnvoll) ---------- */
    // "Undo-Kandidat" = soll immer sichtbar bleiben (auch wenn Reason-Tab aktiv ist)
    function isUndoCandidate(person) {
      return (
        person?.account_status === 'deactivated_pending_deletion' ||
        person?.missing?.includes('undo')
      );
    }

    // Button nur anzeigen, wenn Undo noch möglich ist (scheduled_delete_at nicht überschritten)
    function canSendUndoLink(person) {
      if (!isUndoCandidate(person)) return false;

      if (person?.scheduled_delete_at) {
        const ts = new Date(person.scheduled_delete_at).getTime();
        if (!Number.isNaN(ts) && ts <= Date.now()) return false;
      }
      return true;
    }

  async function sendUndoLink(person) {
    const id = person.user_id;
    setUndoIds(prev => new Set(prev).add(id));

    const req = mentoringApi.post(`/mentoring/hr/users/${id}/undo-link`, {}, { withCredentials: true });

    try {
      const res = await toast.promise(req, {
        loading: tr('missing.undoSending', 'Sende Undo-Link…', 'Sending undo link…'),
        success: tr('missing.undoSent', 'Undo-Link wurde versendet.', 'Undo link sent.'),
        error: tr('missing.undoFail', 'Undo-Link konnte nicht gesendet werden.', 'Failed to send undo link.')
      });

      // Optional: UI aktualisieren (Status bleibt pending, aber wir könnten z.B. expires_at anzeigen)
      // Für Minimal-Change lassen wir Daten as-is.
      // Falls du willst, kannst du hier setList(...) mit res.data mergen.

      // Optional: Link in Clipboard (hilfreich für Support), aber leise/sicher
      const undoUrl = res?.data?.undo_url;
      if (undoUrl && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try { await navigator.clipboard.writeText(undoUrl); } catch {}
      }
    } finally {
      setUndoIds(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }

  if (!surveyId) {
    return (
      <div className="bg-gray-800 p-6 rounded-2xl text-gray-300 mt-8">
        {tr('missing.selectSurvey', 'Bitte zuerst ein Projekt / eine Umfrage auswählen.', 'Please select a project / survey first.')}
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-md mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">
          {tr('missing.heading', 'Teilnehmende mit Handlungsbedarf', 'Participants needing action')}
        </h2>

        {/* Mini-Legende */}
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-400" /> {lang === 'de' ? '3–7 Tage' : '3–7 days'}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500" /> {lang === 'de' ? '≥ 7 Tage' : '≥ 7 days'}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-600" /> {lang === 'de' ? '≥ 14 Tage' : '≥ 14 days'}
          </span>
        </div>
      </div>

      {/* Tabs: Rolle */}
      <div className="flex gap-2 mb-3">
        {ROLE_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setRoleTab(tab.key)}
            className={classNames(
              'px-3 py-1.5 rounded-xl text-sm transition',
              roleTab === tab.key ? 'bg-mentixxgreen text-black font-semibold' : 'bg-gray-700 hover:bg-gray-600 text-white'
            )}
          >
            {t(`missing.role.${tab.labelKey}`, lang === 'de' ? tab.fallbackDe : tab.fallbackEn)}
          </button>
        ))}
      </div>

      {/* Tabs: Grund + Suche + Bulk-Aktionen */}
      <div className="flex gap-2 mb-2 flex-wrap items-center">
        {REASON_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setReasonTab(tab.key)}
            className={classNames(
              'px-3 py-1.5 rounded-xl text-sm transition',
              reasonTab === tab.key ? 'bg-gray-300 text-black font-semibold' : 'bg-gray-700 hover:bg-gray-600 text-white'
            )}
          >
            {t(`missing.reason.${tab.labelKey}`, lang === 'de' ? tab.fallbackDe : tab.fallbackEn)}
          </button>
        ))}

        {/* Dringlichkeits-Chips */}
        <div className="flex gap-2 ml-2 flex-wrap">
          {URGENCY_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setUrgencyTab(tab.key)}
              className={classNames(
                'px-3 py-1.5 rounded-xl text-sm transition',
                urgencyTab === tab.key ? 'bg-gray-300 text-black font-semibold' : 'bg-gray-700 hover:bg-gray-600 text-white'
              )}
              title={
                tab.key === 'soon' ? (lang==='de' ? '3–7 Tage inaktiv' : 'inactive 3–7 days') :
                tab.key === 'due' ? (lang==='de' ? '≥ 7 Tage inaktiv' : 'inactive ≥ 7 days') :
                tab.key === 'critical' ? (lang==='de' ? '≥ 14 Tage inaktiv' : 'inactive ≥ 14 days') :
                undefined
              }
            >
              {(lang === 'de' ? tab.fallbackDe : tab.fallbackEn)}{' '}
              {tab.key !== 'all' && <span className="opacity-80">({urgencyCounts[tab.key] ?? 0})</span>}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tr('missing.search', 'Suche (Name/Email/Abteilung)', 'Search (name/email/department)')}
            className="bg-gray-700 text-white rounded-xl px-3 py-2 w-64"
          />
          <button onClick={selectAllVisible} className="bg-gray-700 hover:bg-gray-600 text-white rounded-xl px-3 py-2">
            {tr('missing.selectVisible', 'Sichtbare auswählen', 'Select visible')}
          </button>
          <button onClick={clearSelection} className="bg-gray-700 hover:bg-gray-600 text-white rounded-xl px-3 py-2">
            {tr('missing.clearSelection', 'Auswahl löschen', 'Clear selection')}
          </button>
          <button onClick={bulkCopy} className="bg-mentixxgreen hover:bg-green-400 text-black rounded-xl px-3 py-2 font-semibold">
            {tr('missing.bulkReminder', 'Ausgewählte erinnern', 'Remind selected')}
          </button>
          <button onClick={exportCSV} className="bg-gray-500 hover:bg-gray-400 text-white rounded-xl px-3 py-2">
            {tr('missing.export', 'CSV exportieren', 'Export CSV')}
          </button>
        </div>
      </div>

      {/* Meldungen */}
      {error && <p className="text-red-400 mb-3">{error}</p>}
      {loading && <p className="text-gray-300">{tr('missing.loading', 'Lade…', 'Loading…')}</p>}

      {!loading && filtered.length === 0 && (
        <div className="text-gray-300">
          {tr('missing.empty', 'Alles erledigt – niemand hängt fest 🎉', 'All done — nobody is stuck 🎉')}
        </div>
      )}

      {/* Kartenliste */}
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((person) => {
          const showUndo = canSendUndoLink(person);

          return (
            <li key={person.user_id} className="bg-gray-700 rounded-xl p-4 flex gap-3 items-center">
              <input
                type="checkbox"
                checked={selected.has(person.user_id)}
                onChange={() => toggleSelect(person.user_id)}
                className="mt-1"
                aria-label={tr('missing.select', 'Auswählen', 'Select')}
              />

              <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center text-white text-lg">
                {(person.first_name?.[0] || '?')}{person.last_name?.[0] || ''}
              </div>

              <div className="flex-1">
                <div className="font-semibold text-white">
                  {person.first_name} {person.last_name}
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-500 text-white uppercase">
                    {person.role}
                  </span>
                </div>
                <div className="text-gray-300 text-sm">{person.email}</div>
                <div className="text-gray-400 text-sm">
                  {person.abteilung || '—'} • {person.job_title || '—'}
                </div>

                <div className="mt-2 flex gap-2 flex-wrap items-center">
                  <Badge ok={!person.missing?.includes('onboarding')} label={tr('missing.badge.onboarding', 'Onboarding', 'Onboarding')} lang={lang} />
                  <Badge ok={!person.missing?.includes('profile')}    label={tr('missing.badge.profile', 'Profil', 'Profile')} lang={lang} />
                  <Badge ok={!person.missing?.includes('analysis')}   label={tr('missing.badge.analysis', 'Analyse', 'Analysis')} lang={lang} />

                  <InactivityBadge
                    days={person._daysInactive}
                    lastAt={person._lastActionAt}
                    urgency={person._urgency}
                    lang={lang}
                  />

                  {person.last_login && (
                    <span className="text-xs text-gray-400 ml-2">
                      {tr('missing.lastLogin', 'Letzter Login', 'Last login')}: {formatDateShort(person.last_login, lang === 'de' ? 'de-DE' : 'en-US')}
                    </span>
                  )}

                  {/* Optional: Deadline-Hinweis (nur wenn pending deletion) */}
                  {person.account_status === 'deactivated_pending_deletion' && person.scheduled_delete_at && (
                    <span className="text-xs text-gray-300 ml-2">
                      {lang === 'de' ? 'Löschung geplant: ' : 'Deletion scheduled: '}
                      {formatDateTime(person.scheduled_delete_at, lang === 'de' ? 'de-DE' : 'en-US')}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {/* Undo-Link senden (nur wenn sinnvoll) */}
                {showUndo && (
                  <button
                    onClick={() => sendUndoLink(person)}
                    disabled={undoIds.has(person.user_id)}
                    className={classNames(
                      'rounded-xl px-3 py-2 font-semibold transition',
                      undoIds.has(person.user_id)
                        ? 'bg-blue-900 text-white opacity-70 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-500 text-white'
                    )}
                  >
                    {undoIds.has(person.user_id)
                      ? tr('missing.undoSending', 'Sende Undo-Link…', 'Sending undo link…')
                      : tr('missing.undo', 'Undo-Link senden', 'Send undo link')}
                  </button>
                )}

                <button
                  onClick={() => openMailClient(person)}
                  className="bg-mentixxgreen hover:bg-green-400 text-black font-semibold rounded-xl px-3 py-2"
                >
                  {tr('missing.remind', 'Erinnern', 'Remind')}
                </button>

                <button
                  onClick={() => copyMail(person)}
                  className="bg-gray-600 hover:bg-gray-500 text-white rounded-xl px-3 py-2"
                >
                  {tr('missing.copy', 'Mail kopieren', 'Copy mail')}
                </button>

                {/* Löschen */}
                <button
                  onClick={() => handleDeleteClick(person)}
                  disabled={deletingIds.has(person.user_id)}
                  className={classNames(
                    'rounded-xl px-3 py-2 font-semibold transition',
                    deletingIds.has(person.user_id)
                      ? 'bg-red-800 text-white opacity-70 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-500 text-white'
                  )}
                >
                  {deletingIds.has(person.user_id)
                    ? tr('missing.deleting', 'Lösche…', 'Deleting…')
                    : tr('missing.delete', 'Löschen', 'Delete')}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}





